<script lang="ts">
  import { api } from '../../lib/api/client';
  import type { PlaceCategory, PlacePatterns } from '../../lib/api/types';
  import Bars from '../../lib/components/charts/Bars.svelte';
  import Chart from '../../lib/components/charts/Chart.svelte';
  import Readout from '../../lib/components/charts/Readout.svelte';
  import { dateTicks, niceTicks } from '../../lib/utils/chart';
  import { count, monthName } from '../../lib/utils/insights';
  import { weekStart } from '../../lib/utils/progress';
  import { shade } from '../../lib/utils/training';
  import { locale, t, weekday } from '../../lib/i18n/index.ts';
  import { categoryIcon, categoryLabel, hourName, placeName, shortDate, span } from './format';
  import YearDays from './YearDays.svelte';

  // PlacePage is when the member is usually at one of their places (spec 005 FR-021): time there by weekday and hour, visit statistics,
  // visits per month, and a calendar of the days with a visit. Its charts
  // are in the shared chart language (spec 010 FR-033).
  let { place, onBack, onDay }: {
    place: { id: string; name?: string; address?: string; category?: PlaceCategory; city?: string };
    onBack: () => void;
    onDay: (date: string) => void;
  } = $props();

  const COLOR = 'var(--theme-accent)';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const ws = weekStart(navigator.language);
  let p = $state<PlacePatterns | null>(null);
  let error = $state('');
  let calYear = $state(new Date().getFullYear());

  $effect(() => {
    const id = place.id;
    p = null;
    api.get<PlacePatterns>(`/me/timeline/places/${id}/patterns?tz=${encodeURIComponent(tz)}`)
      .then((got) => {
        if (got.place_id !== place.id) return;
        p = got;
        error = '';
        if (got.days.length) calYear = Number(got.days[got.days.length - 1].slice(0, 4));
      })
      .catch((e) => (error = e.message));
  });

  // The weekday and hour grid. The server's rows run Monday first; they are
  // shown from the viewer's first day of the week.
  const order = Array.from({ length: 7 }, (_, r) => (ws + 6 + r) % 7);
  // rowDay is a row's weekday name; 1 January 2024 was a Monday.
  const rowDay = (row: number) => weekday(new Date(2024, 0, 1 + row, 12), true);
  const ROW_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const most = $derived(p ? Math.max(0, ...p.hours.flat()) : 0);
  let cellHover = $state<[number, number] | null>(null);
  let cellPick = $state<[number, number] | null>(null);
  const cellShown = $derived(cellHover ?? cellPick);
  let gridEl: HTMLElement | undefined = $state();

  function cellFill(v: number): string {
    const step = shade(v, most);
    if (step <= 0) return 'var(--theme-surface1)';
    return `color-mix(in srgb, ${COLOR} ${[0, 30, 55, 78, 100][step]}%, var(--theme-surface0))`;
  }

  function cellText(row: number, h: number): string {
    const v = p?.hours[row]?.[h] ?? 0;
    const args = { day: ROW_KEYS[row], start: hourName(h), end: hourName((h + 1) % 24) };
    return v ? t('timelineCellSpent', { ...args, time: span(v) }) : t('timelineCellNever', args);
  }

  // cellKey moves the picked hour with the arrow keys: across the hours, and
  // up and down the weekdays in the order shown.
  function cellKey(e: KeyboardEvent, r: number, h: number) {
    if (e.key === 'Escape') {
      cellPick = null;
      e.preventDefault();
      return;
    }
    const at = order.indexOf(r);
    let nr = at, nh = h;
    if (e.key === 'ArrowLeft') nh = Math.max(0, h - 1);
    else if (e.key === 'ArrowRight') nh = Math.min(23, h + 1);
    else if (e.key === 'ArrowUp') nr = Math.max(0, at - 1);
    else if (e.key === 'ArrowDown') nr = Math.min(6, at + 1);
    else return;
    e.preventDefault();
    cellPick = [order[nr], nh];
    gridEl?.querySelector<HTMLElement>(`[data-cell="${order[nr]}-${nh}"]`)?.focus();
  }

  // The visits per month chart.
  let at = $state<number | null>(null);
  let hover = $state<number | null>(null);
  const months = $derived(p?.months ?? []);
  const scale = $derived(niceTicks(0, Math.max(0, ...months.map((m) => m.visits)), { zero: true, whole: true }));
  const positions = $derived(months.map((_, i) => (i + 0.5) / months.length));
  const xTicks = $derived.by(() => {
    if (!months.length) return [];
    const first = `${months[0].month}-01`, last = `${months[months.length - 1].month}-01`;
    const t = dateTicks(first, last, 6, ws);
    return t.days.map((d) => {
      const i = months.findIndex((m) => m.month === d.slice(0, 7));
      return { at: positions[i], text: new Date(`${d}T12:00:00`).toLocaleDateString(locale(), t.unit === 'year' || d.endsWith('-01-01') ? { month: 'short', year: '2-digit' } : { month: 'short' }) };
    }).filter((t) => t.at != null);
  });
  const reading = $derived(hover ?? at);
  const monthReading = $derived(reading != null ? months[reading] : null);
  const visitsText = (n: number) => t('timelineVisitCount', { n });

  // The visit days calendar.
  const visitDays = $derived(new Map((p?.days ?? []).map((d) => [d, 1])));
  const firstYear = $derived(p?.days.length ? Number(p.days[0].slice(0, 4)) : calYear);
  const lastYear = $derived(p?.days.length ? Number(p.days[p.days.length - 1].slice(0, 4)) : calYear);
  const weeks = (n: number) => t('timelineWeekCount', { n });
  const dateLong = (iso: string) => new Date(iso).toLocaleDateString(locale(), { month: 'long', day: 'numeric', year: 'numeric' });
</script>

<div class="flex-1 overflow-y-auto p-4 sm:p-6">
  <div class="max-w-4xl mx-auto space-y-4">
    <button class="text-sm hover:opacity-80" onclick={onBack}><i aria-hidden="true" class="las la-arrow-left mr-1"></i>{t('timelineTabPlaces')}</button>
    <div class="flex items-center gap-3">
      <i aria-hidden="true" class="las {categoryIcon(place.category)} text-3xl"></i>
      <div class="min-w-0">
        <h2 class="text-lg font-semibold truncate">{placeName(place)}</h2>
        <p class="text-sm text-text-muted truncate">{[place.name ? place.address : '', place.city, place.category ? categoryLabel(place.category) : ''].filter(Boolean).join(' · ')}</p>
      </div>
    </div>

    {#if error}
      <p class="text-sm" style="color: var(--theme-error);">{error}</p>
    {:else if !p}
      <p class="text-sm text-text-muted">{t('timelineLoading')}</p>
    {:else if p.visits === 0}
      <p class="card p-5 text-sm text-text-muted">{t('timelinePlaceNoVisits')}</p>
    {:else}
      <section class="card p-5">
        <dl class="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div><dt class="text-xs text-text-muted">{t('timelineVisits')}</dt><dd class="text-xl font-semibold tabular-nums">{count(p.visits)}</dd></div>
          <div><dt class="text-xs text-text-muted">{t('timelineTotalTime')}</dt><dd class="text-xl font-semibold tabular-nums">{span(p.total_s)}</dd></div>
          <div><dt class="text-xs text-text-muted">{t('timelineAverageVisit')}</dt><dd class="text-xl font-semibold tabular-nums">{span(p.average_s)}</dd></div>
          <div><dt class="text-xs text-text-muted">{t('timelineLongestVisit')}</dt><dd class="text-xl font-semibold tabular-nums">{span(p.longest_s)}</dd></div>
          {#if p.first_visit}<div><dt class="text-xs text-text-muted">{t('timelineFirstVisit')}</dt><dd class="font-medium">{shortDate(p.first_visit)}</dd></div>{/if}
          {#if p.last_visit}<div><dt class="text-xs text-text-muted">{t('timelineLastVisitLabel')}</dt><dd class="font-medium">{shortDate(p.last_visit)}</dd></div>{/if}
          <div class="col-span-2"><dt class="text-xs text-text-muted">{t('timelineWeekStreak')}</dt><dd class="font-medium">{weeks(p.longest_week_streak)}</dd></div>
        </dl>
      </section>

      <section class="card p-5 space-y-2" aria-labelledby="hours-heading">
        <h2 id="hours-heading" class="font-semibold">{t('timelineWhenThere')}</h2>
        <Readout title={cellShown ? cellText(cellShown[0], cellShown[1]) : t('timelineHoverHour')} idle={!cellShown} items={[]} />
        <div bind:this={gridEl} class="grid gap-[2px] text-[11px] text-text-muted tabular-nums" style="grid-template-columns: auto repeat(24, minmax(0, 1fr));"
          role="group" aria-label={t('timelinePlaceGrid', { place: placeName(place) })} onmouseleave={() => (cellHover = null)}>
          {#each order as r (r)}
            <span class="pr-1.5 leading-4 whitespace-nowrap" aria-hidden="true">{rowDay(r)}</span>
            {#each p.hours[r] as v, h (h)}
              {@const on = cellPick?.[0] === r && cellPick?.[1] === h}
              <button class="h-4 rounded-[2px] hover:ring-1 {on ? 'ring-2' : ''}" style="background: {cellFill(v)}; --tw-ring-color: var(--theme-text);"
                data-cell="{r}-{h}" tabindex={(cellPick ? on : r === order[0] && h === 0) ? 0 : -1}
                aria-label={cellText(r, h)} aria-pressed={on}
                onmouseenter={() => (cellHover = [r, h])} onclick={() => (cellPick = [r, h])} onkeydown={(e) => cellKey(e, r, h)}></button>
            {/each}
          {/each}
          <span aria-hidden="true"></span>
          {#each Array.from({ length: 24 }, (_, h) => h) as h (h)}
            <span class="leading-4 whitespace-nowrap overflow-visible" aria-hidden="true">{h % 6 === 0 ? hourName(h) : ''}</span>
          {/each}
        </div>
        <div class="flex items-center justify-end gap-1 text-xs text-text-muted" aria-hidden="true">
          {t('timelineNever')}
          {#each [0, 1, 2, 3, 4] as s (s)}<span class="w-[11px] h-[11px] rounded-[2px]" style="background: {s === 0 ? 'var(--theme-surface1)' : `color-mix(in srgb, ${COLOR} ${[0, 30, 55, 78, 100][s]}%, var(--theme-surface0))`};"></span>{/each}
          {t('timelineMost')}
        </div>
      </section>

      {#if months.length}
        <section class="card p-5 space-y-2" aria-labelledby="months-heading">
          <h2 id="months-heading" class="font-semibold">{t('timelineVisitsPerMonth')}</h2>
          <Readout title={monthReading ? t('timelineReading', { label: monthName(monthReading.month), value: visitsText(monthReading.visits) }) : t('timelineHoverMonth')} idle={!monthReading}
            items={[{ label: t('timelineVisits'), color: COLOR, mark: 'bar' as const }]} />
          <Chart lo={scale.lo} hi={scale.hi} yTicks={scale.ticks.map((t) => ({ v: t, text: count(t) }))} unit={t('timelineVisitsUnit')}
            {xTicks} xGrid={false} height="h-32" {positions} bind:at bind:hover
            label={t('timelineVisitsPerMonthTo', { place: placeName(place) })}
            valueText={monthReading ? t('timelineReading', { label: monthName(monthReading.month), value: visitsText(monthReading.visits) }) : t('timelineNoMonthPicked')}>
            {#snippet overlay()}
              <Bars bars={months.map((m, i) => ({ at: positions[i], v: m.visits, color: COLOR }))} width={0.7 / months.length} lo={scale.lo} hi={scale.hi} picked={at} />
            {/snippet}
          </Chart>
        </section>
      {/if}

      <section class="card p-5 space-y-2" aria-labelledby="days-heading">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h2 id="days-heading" class="font-semibold">{t('timelineDaysWithVisit')}</h2>
          <div class="flex items-center gap-1 text-sm">
            <button class="btn btn-secondary btn-sm" aria-label={t('timelineYearBefore')} disabled={calYear <= firstYear} onclick={() => calYear--}><i aria-hidden="true" class="las la-chevron-left"></i></button>
            <span class="px-1 font-medium" aria-live="polite">{calYear}</span>
            <button class="btn btn-secondary btn-sm" aria-label={t('timelineYearAfter')} disabled={calYear >= lastYear} onclick={() => calYear++}><i aria-hidden="true" class="las la-chevron-right"></i></button>
          </div>
        </div>
        <YearDays year={calYear} values={visitDays} max={1} color={COLOR} binary name={t('timelineDaysOfWithVisit', { year: String(calYear) })}
          text={(v) => (v ? t('timelineVisited') : t('timelineNotVisited'))} onOpen={onDay} />
        {#if p.first_visit && p.last_visit}
          <p class="text-xs text-text-muted">{t('timelineFirstLast', { first: dateLong(p.first_visit), last: dateLong(p.last_visit) })}</p>
        {/if}
      </section>
    {/if}
  </div>
</div>
